import React, { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const CONDITIONS = ['Clear', 'Rain', 'Wind', 'Storm', 'Heat', 'Cold', 'Fog', 'Other'];

type Props = { projectId?: string };

export const WeatherPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const weatherQ = useQuery({
    queryKey: ['cep-weather', projectId],
    queryFn: () => epcService.getCepWeather(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createWeather({ ...values, projectId }),
    onSuccess: () => {
      message.success('Weather observation logged');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-weather'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
      void qc.invalidateQueries({ queryKey: ['cep-dashboards'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for weather observations</Tag>;

  const rows = (weatherQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Log weather
        </Button>
        <Tag color="geekblue">CEP Wave 5</Tag>
        <Tag>Work-stop feeds Waiting_Weather readiness (24h)</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={weatherQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Observed', dataIndex: 'observed_at', width: 160 },
          { title: 'Condition', dataIndex: 'condition_code', width: 100 },
          { title: 'Temp °C', dataIndex: 'temperature_c', width: 80 },
          { title: 'Rain mm', dataIndex: 'rainfall_mm', width: 80 },
          {
            title: 'Work stopped',
            dataIndex: 'work_stopped',
            width: 110,
            render: (v: number) => (v ? <Tag color="red">Yes</Tag> : <Tag>No</Tag>),
          },
          { title: 'Notes', dataIndex: 'notes', ellipsis: true },
        ]}
      />
      <Modal
        title="Log weather observation"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{ conditionCode: 'Rain', workStopped: false }}
        >
          <Form.Item name="conditionCode" label="Condition" rules={[{ required: true }]}>
            <Select options={CONDITIONS.map((c) => ({ value: c, label: c }))} />
          </Form.Item>
          <Form.Item name="temperatureC" label="Temperature °C">
            <InputNumber style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="windSpeed" label="Wind speed">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="rainfallMm" label="Rainfall mm">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="workStopped" label="Work stopped" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default WeatherPanel;
